<template>
    <el-form class="product-form__package-info">
        <div class="carton-price-input">
            <span
                >What's the price for a carton of {{ productData.name }}?
            </span>
            <el-input-number
                :controls="false"
                v-model="cartonPrice"
                :precision="2"
                placeholder="$0.00"
                @keyup.native="handleUpdateCartonPrice"
            ></el-input-number>
        </div>

        <div class="existing-groups">
            <existing-group-row
                v-for="group in productData.groups"
                :key="group.depth"
                v-bind="group"
                @update:group="updateGroup"
            ></existing-group-row>
        </div>

        <div class="group-type-input">
            <current-group-row
                v-if="lastGroup.type !== 'UNIT'"
                :has-at-least-one-group="hasAtLeastOneGroup"
                v-bind.sync="currentGroup"
                @click:add="addGroup"
            >
            </current-group-row>
        </div>

        <div v-if="lastGroup.type === 'UNIT'" class="unit-inputs">
            <h4 style="font-weight: 100;">Describe the base unit</h4>
            <unit-inputs
                v-bind.sync="productData"
                @update:unitType="$emit('update:unitType', $event)"
                @update:unitOfMeasurement="
                    $emit('update:unitOfMeasurement', $event)
                "
                @update:unitSize="$emit('update:unitSize', $event)"
            ></unit-inputs>
            <h2>Cost Per Unit: ${{ productData.unitPrice.toFixed(2) }}</h2>
        </div>
    </el-form>
</template>

<script>
import UnitDropdownOptionsMixin from "Mixins/UnitDropdownOptionsMixin";

import UnitInputs from "Components/UnitInputs";

import CurrentGroupRow from "./CurrentGroupRow";
import ExistingGroupRow from "./ExistingGroupRow";
import ProductFormInputRow from "./ProductFormInputRow";

export default {
    components: {
        CurrentGroupRow,
        ExistingGroupRow,
        ProductFormInputRow,
        UnitInputs,
    },

    mixins: [UnitDropdownOptionsMixin],

    props: {
        productData: Object,
    },

    data: () => ({
        _cartonPrice: null,
        groups: [],

        currentGroup: {
            quantity: 1,
            type: "",
            depth: 0,
        },

        typeOptions: [
            {
                value: "UNIT",
                label: "Units",
            },
            {
                value: "PACKAGE",
                label: "Packages",
            },
        ],
    }),

    computed: {
        hasAtLeastOneGroup() {
            return this.productData.groups.length > 0;
        },

        lastGroup() {
            if (this.groups.length > 0) {
                return this.groups[this.groups.length - 1];
            }

            return {};
        },

        cartonPrice: {
            get() {
                if (!this._cartonPrice) {
                    return (
                        this.productData.unitPrice *
                        this._getUnitQuantityPerCarton(this.groups)
                    );
                }
                return this._cartonPrice;
            },
            set(val) {
                if (isNaN(val)) {
                    val = 0;
                }
                if (isNaN(Number(val))) {
                    val = val.replace(/[^0-9.]/g, "");
                }

                this._cartonPrice = val;

                if (this._getUnitQuantityPerCarton(this.groups)) {
                    this.productData.unitPrice =
                        this._cartonPrice /
                        this._getUnitQuantityPerCarton(this.groups);
                }
                return this._cartonPrice;
            },
        },
    },

    mounted() {
        this.groups = this.productData.groups;
        this.initCurrentGroup();
    },

    methods: {
        initCurrentGroup() {
            if (this.groups.length > 0) {
                this.currentGroup.depth =
                    this.groups[this.groups.length - 1].depth + 1;
            }
        },

        handleUpdateCartonPrice(event) {
            this.cartonPrice = event.target.value;
        },

        /*
            Determines if there is enough data in the groups array to calculate how many units are in a carton.
            Conditions:
                - The must be inputting a unit type group
                - The UNIT type group needs a quantity specified
             */
        _getUnitQuantityPerCarton(groups) {
            if (this.lastGroup.type === "UNIT") {
                return groups.reduce(
                    (total, group) => total * group.quantity,
                    1
                );
            }

            return null;
        },

        addGroup(groupToAdd) {
            this.groups.push(groupToAdd);
            this._setCurrentGroup(this.currentGroup.depth + 1);

            this.productData.unitPrice =
                this._cartonPrice / this._getUnitQuantityPerCarton(this.groups);
            this.$emit("update:groups", this.groups);
        },

        updateGroup(updatedGroup, prevGroup) {
            if (prevGroup.type === "PACKAGE" && updatedGroup.type === "UNIT") {
                this.groups = this.groups.slice(0, updatedGroup.depth);

                // resetting the depths on the group
                this.groups = this.groups.map((group, index) => {
                    return {
                        ...group,
                        depth: index,
                    };
                });

                this._setCurrentGroup(
                    this.groups.length,
                    "UNIT",
                    updatedGroup.quantity
                );
            } else {
                this.groups = this.groups.map(group => {
                    if (group.depth === updatedGroup.depth) {
                        return updatedGroup;
                    }

                    return group;
                });

                this.productData.unitPrice =
                    this._cartonPrice /
                    this._getUnitQuantityPerCarton(this.groups);
            }

            this.$emit("update:groups", this.groups);
        },

        _setCurrentGroup(depth = 0, type = "", quantity = 0) {
            this.currentGroup = {
                quantity,
                type,
                depth,
            };
        },
    },
};
</script>

<style scoped lang="scss">
/deep/ input.el-input__inner {
    text-align: left;
}

.carton-price-input {
    margin-bottom: 20px;
}
</style>
